/**
 * DeactivateAgentDialog — shown when an ACTIVE agent is about to be turned off.
 *
 * An agent can quietly be the answer for several things at once: the general fallback for
 * unmatched WhatsApp messages, the pinned WhatsApp agent, the pinned Instagram agent, and
 * the agent behind the public web chat. Switching it off without looking leaves those
 * channels with nobody to answer (the backend then falls back to an arbitrary pick, or to
 * nothing). So this lists exactly what the agent currently covers and lets the admin
 * choose, per item, another agent to take over — or leave it unassigned — before confirming.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ChatOutlined,
  InfoOutlined,
  Instagram,
  LanguageOutlined,
  PowerSettingsNewOutlined,
  StarOutline,
  WhatsApp,
} from '@mui/icons-material';
import { CHANNEL_TONE } from './channelTones';

export type AgentUsageKey = 'fallback' | 'whatsapp' | 'instagram' | 'webChat';

export interface AgentUsage {
  key: AgentUsageKey;
  /** Short name of the role, e.g. "WhatsApp default agent". */
  title: string;
  /** What stops working if nobody replaces it. */
  description: string;
}

export const USAGE_COPY: Record<AgentUsageKey, Omit<AgentUsage, 'key'>> = {
  fallback: {
    title: 'Default fallback agent',
    description: 'Answers any WhatsApp message that matches no other agent.',
  },
  whatsapp: {
    title: 'WhatsApp default agent',
    description: 'Pinned to answer every inbound WhatsApp message.',
  },
  instagram: {
    title: 'Instagram agent',
    description: 'Pinned to answer every Instagram DM.',
  },
  webChat: {
    title: 'Web chat agent',
    description: 'Powers your public web chat page and widget.',
  },
};

const USAGE_ICON: Record<AgentUsageKey, React.ReactNode> = {
  fallback: <StarOutline fontSize="small" />,
  whatsapp: <WhatsApp fontSize="small" />,
  instagram: <Instagram fontSize="small" />,
  webChat: <LanguageOutlined fontSize="small" />,
};

interface Candidate {
  id: string;
  name: string;
}

interface Props {
  open: boolean;
  agentName: string;
  usages: AgentUsage[];
  /** Other ACTIVE agents that could take over. */
  candidates: Candidate[];
  busy?: boolean;
  error?: string;
  /** When the dialog came from the edit form, the change is saved with "Update" there. */
  fromEditForm?: boolean;
  onCancel: () => void;
  /** replacements[usageKey] = agent id to hand over to, or '' for "leave unassigned". */
  onConfirm: (replacements: Partial<Record<AgentUsageKey, string>>) => void;
}

const DeactivateAgentDialog: React.FC<Props> = ({
  open,
  agentName,
  usages,
  candidates,
  busy = false,
  error = '',
  fromEditForm = false,
  onCancel,
  onConfirm,
}) => {
  const [choices, setChoices] = useState<Partial<Record<AgentUsageKey, string>>>({});

  useEffect(() => {
    if (open) setChoices({});
  }, [open, agentName]);

  const hasUsages = usages.length > 0;
  const unassignedCount = useMemo(
    () => usages.filter((u) => !(choices[u.key] ?? '')).length,
    [usages, choices]
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(79, 70, 229, 0.08)',
            color: '#4f46e5',
            flexShrink: 0,
          }}
        >
          <PowerSettingsNewOutlined />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          Deactivate {agentName}?
          <Typography variant="body2" color="text.secondary" fontWeight={400}>
            It will stop answering on every channel.
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {hasUsages ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              This agent is currently used for the following. Choose who should take over each one — or leave
              it unassigned.
            </Typography>
            <Stack spacing={1.25}>
              {usages.map((u) => (
                <Box
                  key={u.key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    border: '1px solid #e8ecf3',
                    borderRadius: 2.5,
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1.75,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: CHANNEL_TONE[u.key].bg,
                      color: CHANNEL_TONE[u.key].fg,
                    }}
                  >
                    {USAGE_ICON[u.key] ?? <ChatOutlined fontSize="small" />}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {u.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {u.description}
                    </Typography>
                  </Box>
                  <TextField
                    select
                    size="small"
                    value={choices[u.key] ?? ''}
                    disabled={busy}
                    onChange={(e) => setChoices((prev) => ({ ...prev, [u.key]: e.target.value }))}
                    sx={{ minWidth: 190 }}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (v) =>
                        v ? (
                          candidates.find((c) => c.id === v)?.name ?? String(v)
                        ) : (
                          <Box component="span" sx={{ color: 'text.disabled' }}>
                            Leave unassigned
                          </Box>
                        ),
                    }}
                  >
                    <MenuItem value="">
                      <Box component="span" sx={{ color: 'text.secondary' }}>
                        Leave unassigned
                      </Box>
                    </MenuItem>
                    {candidates.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              ))}
            </Stack>
            {unassignedCount > 0 && (
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  gap: 1.25,
                  alignItems: 'flex-start',
                  p: 1.5,
                  borderRadius: 2.5,
                  bgcolor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  color: '#475569',
                }}
              >
                <InfoOutlined sx={{ fontSize: 18, mt: '1px', color: '#64748b' }} />
                <Typography variant="body2" sx={{ color: 'inherit' }}>
                  {unassignedCount === 1
                    ? '1 role will have no agent assigned.'
                    : `${unassignedCount} roles will have no agent assigned.`}{' '}
                  Customers on those channels won&apos;t get an AI reply until you assign one.
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            This agent isn&apos;t the default fallback or pinned to WhatsApp, Instagram or web chat, so turning
            it off won&apos;t leave any channel without an answer.
          </Typography>
        )}

        {fromEditForm && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Assignments change right away. The agent itself is switched off when you click <strong>Update</strong>
            .
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={busy}
          onClick={() => onConfirm(choices)}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <PowerSettingsNewOutlined />}
        >
          {hasUsages ? 'Apply & deactivate' : 'Deactivate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeactivateAgentDialog;
